import React, { useState, useEffect } from 'react';
import { FaGoogle } from 'react-icons/fa';
import { Modal } from 'react-bootstrap';
import Slide1Edit from '../slides/slide1Edit/Slide1Edit';
import GoogleReviewSlideEdit from '../slides/googleReviewSlideEdit/GoogleReviewSlideEdit';
import PollingEdit from '../polling/PollingEdit';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSlides, deleteSlide, saveSlide } from '../../redux/slidesSlice';
import { deleteDeck } from '../../redux/decksSlice';
import { sampleSlide1, sampleSlide2, sampleSlide3 } from '../../config/DefaultSlides';
import './deck.css';
import { notifyError, notifySuccess } from '../Notification';
import { faEdit, faTrash, faTrashAlt, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchDecks } from '../../redux/decksSlice';
import { fetchStores } from '../../redux/storesSlice';
import { api } from '../../api/index';
import GenericModal from '../genericModal/GenericModal';
import AdUploadModal from '../adUploadModal/AdUploadModal';
import ComplaintModal from '../complaintModal/ComplaintModal';

const Deck = ({ deck }) => {
  const dispatch = useDispatch();
  const { slides } = useSelector((state) => state.slides);
  const { items: stores } = useSelector((state) => state.stores);
  const [selectedSlide, setSelectedSlide] = useState(null);
  const [deckLabel, setDeckLabel] = useState(deck?.deckName);
  const [isEditingDeckName, setIsEditingDeckName] = useState(false);
  const [addSlide, setAddSlide] = useState(false);
  const [selectedSlideNumber, setSelectedSlideNumber] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [viewDeck, setViewDeck] = useState(false);
  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [deckToDelete, setDeckToDelete] = useState(null);
  const [showAdUploadModal, setShowAdUploadModal] = useState(false);
  const [deckAds, setDeckAds] = useState([]);
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [showPollingSlide, setShowPollingSlide] = useState(false);
  const [pollingData, setPollingData] = useState(null);

    // ✅ Set default complaint options
  const DEFAULT_COMPLAINT_OPTIONS = [
    'Unfriendly Staff',
    'Products Unavailable',
    'Long Wait Times',
    'Store Cleanliness',
  ];

  const [complaintOptions, setComplaintOptions] = useState(DEFAULT_COMPLAINT_OPTIONS);
  const [isComplaintSetActive, setIsComplaintSetActive] = useState(false);
  const [complaintSetExists, setComplaintSetExists] = useState(false);

  // NEW: Deck Features State
  const [deckFeatures, setDeckFeatures] = useState({
    complaint_slide_enabled: false,
    google_review_slide_enabled: false,
    ads_enabled: true,
    complement_slide_enabled: false,
    polling_slide_enabled: false
  });

  const fetchDeckAds = async () => {
      if (deck?.deckId) {
        try {
          const response = await api.get(`/ads/${deck.deckId}`);
          setDeckAds(response.data);
        } catch (error) {
          console.error("Error fetching deck ads:", error);
          notifyError("Error fetching ad images.");
        }
      }
    };

  useEffect(() => {
    fetchDeckAds();
  }, [deck?.deckId]);

 const fetchComplaintOptions = async () => {
    if (deck?.deckId) {
      try {
        const response = await api.get(`/complaint-options/${deck.deckId}`);
        
        // ✅ Only update if complaint set exists, otherwise keep defaults
        if (response.data.exists && response.data.complaints?.length > 0) {
          setComplaintOptions(response.data.complaints);
          setComplaintSetExists(true);
        } else {
          setComplaintOptions(DEFAULT_COMPLAINT_OPTIONS);
          setComplaintSetExists(false);
        }
        
        setIsComplaintSetActive(response.data.is_active || false);
      } catch (error) {
        console.error("Error fetching complaint options:", error);
        // Keep default options on error
        setComplaintOptions(DEFAULT_COMPLAINT_OPTIONS);
      }
    }
  };

  // NEW: Fetch Deck Features
  const fetchDeckFeatures = async () => {
    if (deck?.deckId) {
      try {
        const response = await api.get(`/decks/${deck.deckId}/features`);
        setDeckFeatures(response.data);

        // Also update individual states for backward compatibility
        setIsComplaintSetActive(response.data.complaint_slide_enabled || false);
      } catch (error) {
        console.error("Error fetching deck features:", error);
        // Don't show error notification - features are optional
      }
    }
  };

  // Fetch Polling Data for the deck
  const fetchPollingData = async () => {
    if (!deck?.deckId) return;
    try {
      const response = await api.get(`/polls/${deck.deckId}`);
      // Backend returns { success: true, data: poll_object_or_null }
      if (response.data.data) {
        setPollingData(response.data.data);
        console.log('✅ Polling data loaded:', response.data.data);
      } else {
        setPollingData(null);
        console.log('✅ No polling data found for deck, showing blank form');
      }
    } catch (error) {
      console.error('Error fetching polling data:', error);
      setPollingData(null);
    }
  };

  // UPDATED: useEffect to fetch both complaint options and deck features
  useEffect(() => {
    console.log("Fetching complaint options and deck features for deckId:", deck?.deckId);
    fetchComplaintOptions();
    fetchDeckFeatures();
  }, [deck?.deckId]);

  // Handle complaint set toggle (only works when complaint set exists)
  const handleComplaintSetToggle = async (checked) => {
    try {
      await api.patch(`/complaint-options/${deck.deckId}/toggle`, {
        is_active: checked
      });
      setIsComplaintSetActive(checked);
      notifySuccess(checked ? 'Complaint slide enabled.' : 'Complaint slide disabled.');
    } catch (error) {
      console.error('Error toggling complaint set:', error);
      notifyError('Error updating complaint slide status.');
    }
  };

  // NEW: Handle Feature Toggle
  const handleFeatureToggle = async (featureName, enabled) => {
    try {
      // Special handling for complaint_slide_enabled
      if (featureName === 'complaint_slide_enabled') {
        if (enabled && !complaintSetExists) {
          // Auto-create complaint set with default options when enabling
          console.log('Creating default complaint set...');
          
          try {
            await api.post('/complaint-options', {
              deck_id: deck.deckId,
              complaints: DEFAULT_COMPLAINT_OPTIONS,
              is_active: true
            });

            // Refetch to get the newly created complaint set
            await fetchComplaintOptions();
            await fetchDeckFeatures();
            
            notifySuccess('Complaint Slide enabled with default options.');
            return;
          } catch (error) {
            console.error('Error creating default complaint set:', error);
            notifyError('Error enabling Complaint Slide.');
            return;
          }
        } else if (enabled && complaintSetExists) {
          // Just toggle the existing complaint set
          await api.patch(`/complaint-options/${deck.deckId}/toggle`, {
            is_active: true
          });
          setIsComplaintSetActive(true);
          notifySuccess('Complaint Slide enabled.');
        } else {
          // Disable complaint slide
          await api.patch(`/complaint-options/${deck.deckId}/toggle`, {
            is_active: false
          });
          setIsComplaintSetActive(false);
          notifySuccess('Complaint Slide disabled.');
        }

        // Update deck features
        await api.patch(`/decks/${deck.deckId}/features/complaint_slide_enabled`, {
          enabled: enabled
        });

        setDeckFeatures(prev => ({
          ...prev,
          complaint_slide_enabled: enabled
        }));

        return;
      }

      // Handle other features normally
      await api.patch(`/decks/${deck.deckId}/features/${featureName}`, {
        enabled: enabled
      });
      
      setDeckFeatures(prev => ({
        ...prev,
        [featureName]: enabled
      }));
      
      const featureNames = {
        'google_review_slide_enabled': 'Google Review Slide',
        'ads_enabled': 'Ad Images',
        'complement_slide_enabled': 'Complement Slide',
        'polling_slide_enabled': 'Polling Slide'
      };
      
      notifySuccess(`${featureNames[featureName]} ${enabled ? 'enabled' : 'disabled'}.`);
    } catch (error) {
      console.error('Error toggling feature:', error);
      notifyError('Error updating feature status.');
    }
  };

  // Handle complaint options save
  const handleComplaintSave = async (complaints) => {
    try {
      const response = await api.post('/complaint-options', {
        deck_id: deck.deckId,
        complaints: complaints,
        is_active: true
      });

      notifySuccess('Complaint options saved successfully!');
      
      // Refetch complaint options and features to get updated data
      await fetchComplaintOptions();
      await fetchDeckFeatures();
      
      // Close modal after successful save
      setShowComplaintModal(false);
    } catch (error) {
      notifyError('Error saving complaint options.');
      console.error('Error saving complaint set:', error);
      throw error;
    }
  };

  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [loadingStoreDeckAssignment, setLoadingStoreDeckAssignment] = useState(false);

  // New state for modal assignment
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSelectedStoreIds, setTempSelectedStoreIds] = useState([]);

  const allStoreIds = stores.map(store => store.store_id);
  const googleReviewSlide = localStorage.getItem('googleReviewSlide');

  // Get assigned stores for display
  const assignedStores = stores.filter(store => selectedStoreIds.includes(store.store_id));

  // Filter stores based on search term
  const filteredStores = stores.filter(store =>
    store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (store.description && store.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );


  const openAssignmentModal = () => {
    setTempSelectedStoreIds([...selectedStoreIds]);
    setSearchTerm('');
    setShowAssignmentModal(true);
  };

  const closeAssignmentModal = () => {
    setShowAssignmentModal(false);
    setSearchTerm('');
  };

  const toggleTempSiteAssignment = (storeId) => {
    setTempSelectedStoreIds(prev => 
      prev.includes(storeId)
        ? prev.filter(id => id !== storeId)
        : [...prev, storeId]
    );
  };

  const selectAllSites = () => {
    setTempSelectedStoreIds([...allStoreIds]);
  };

  const selectNoSites = () => {
    setTempSelectedStoreIds([]);
  };

  const saveAssignments = async () => {
    if (tempSelectedStoreIds.length === 0) {
      notifyError("Please select at least one site.");
      return;
    }

    try {
      setLoadingStoreDeckAssignment(true);
      const response = await api.post('/deck-store-assignment', { 
        deck_id: deck?.deckId,
        store_ids: tempSelectedStoreIds,
      });
      
      setSelectedStoreIds(tempSelectedStoreIds);
      setLoadingStoreDeckAssignment(false);
      closeAssignmentModal();
      
      console.log("deck-store-assignment response:", response.data);
      notifySuccess("Sites assigned successfully.");
    } catch (error) {
      console.error("Error applying stores:", error);
      notifyError("Error applying stores.");
      setLoadingStoreDeckAssignment(false);
    }
  };

  useEffect(() => {
    const fetchAssignedStores = async () => {
      try {
        const response = await api.get(`/deck-store-assignment?deck_id=${deck?.deckId}`);
        setSelectedStoreIds(response.data);
      } catch (error) {
        console.error("Error fetching store assignments", error);
      }
    };

    fetchAssignedStores();
  }, [deck?.deckId, googleReviewSlide]);

  useEffect(() => {
    const currentSlide = slides.find(s => s.slide_number === selectedSlideNumber);
    if (currentSlide) {
      setSelectedSlide(currentSlide);
    } else if (slides.length > 0) {
      setSelectedSlide(slides[0]);
      setSelectedSlideNumber(slides[0].slide_number);
    } else {
      setSelectedSlide(sampleSlide1);
      setSelectedSlideNumber(1);
    }
  }, [slides, selectedSlideNumber]);

  useEffect(() => {
    dispatch(fetchStores(localStorage.getItem('selectedBusinessId')));
  }, [dispatch]);

  const handleEdit = () => {
    dispatch(fetchSlides(deck.deckId));
   
    setViewDeck(false);
    setShowModal(true);
    setAddSlide(false);
  };

  const handleView = () => {
    dispatch(fetchSlides(deck.deckId));
   
    setViewDeck(true);
    setShowModal(true);
  };

  const handleDelete = () => {
    const confirmDelete = window.confirm(`Are you sure you want to delete the deck: ${deck.deckName}?`);
    if (!confirmDelete) return;

    dispatch(deleteDeck(deck.deckId))
      .unwrap()
      .then(() => {
        notifySuccess(`Deck "${deck.deckName}" deleted successfully`);
        const userEmail = localStorage.getItem('user');
        dispatch(fetchDecks({
          userEmail,
          selectedBusinessId: localStorage.getItem("selectedBusinessId")
        }));
      })
      .catch((error) => {
        notifyError(`Error deleting deck ${deck.deckName}: ${error.message || error}`);
      });
  };

  const handleAddSlide = () => {
    if (showGoogleModal) {
      handleCancelGoogleReviewSlide();
      return;
    }
    setAddSlide(true);
    setShowGoogleModal(prev => !prev);
    setShowPollingSlide(false);
  };

  const handleSlideSelection = (num) => {
    setSelectedSlideNumber(num);
    setSelectedSlide(slides.find((slide) => slide.slide_number === num) || sampleSlide1);
    setShowPollingSlide(false);
  };

  const handleDeckNameChange = (e) => {
    setDeckLabel(e.target.value);
  };

  const saveEditedSlide = (slideData) => {
    const updatedSlide = { ...slideData, deck_name: deckLabel, business_id: localStorage.getItem('selectedBusinessId') };
    dispatch(saveSlide(updatedSlide))
      .unwrap()
      .then(() => {
        notifySuccess('Slide updated successfully!');
        setAddSlide(false);
      })
      .catch((error) => {
        notifyError(`Error updating slide: ${error.message || error}`);
      });
  };

  const deleteSlideHandler = (slideNumber) => {
    dispatch(deleteSlide({ deckId: deck.deckId, slideNumber }));
  };

  const availableSlides = [1, 2, 3].filter((num) => slides.some((slide) => slide.slide_number === num));

  const getNewSlide = () => {
    if (slides.length === 0) {
      return sampleSlide1;
    } else if (slides.length === 1) {
      return sampleSlide2;
    } else if (slides.length === 2) {
      return sampleSlide3;
    }
    return null;
  };

  const handleCancelGoogleReviewSlide = () => {
    setAddSlide(false);
    setShowGoogleModal(false);
    setSelectedSlideNumber(1);
    setSelectedSlide(slides[0] || sampleSlide1);
  };

  const handleOpenPollingSlide = async () => {
    await fetchPollingData();
    setShowPollingSlide(true);
  };

  const handleCancelPollingSlide = () => {
    setShowPollingSlide(false);
    setPollingData(null);
  };

  const handlePollingSlideSuccess = (pollId) => {
    // Notification already shown by PollingEdit component
    // Keep polling slide view open - let user explicitly navigate away
    // Just reset the polling data so it reloads fresh on next edit
  };

  const handleConfirmDelete = () => {
    if (deckToDelete) {
      dispatch(deleteDeck(deckToDelete.deckId))
        .unwrap()
        .then(() => {
          notifySuccess(`Deck "${deckToDelete.deckName}" deleted successfully`);
          const userEmail = localStorage.getItem('user');
          dispatch(fetchDecks({
            userEmail,
            selectedBusinessId: localStorage.getItem("selectedBusinessId")
          }));
        })
        .catch((error) => {
          notifyError(`Error deleting deck ${deckToDelete.deckName}: ${error.message || error}`);
        })
        .finally(() => {
          setShowConfirmDeleteModal(false);
          setDeckToDelete(null);
        });
    }
  };

  const handleAdImageUpload = async (adImages) => {
    const formData = new FormData();
    formData.append('deck_id', deck.deckId);
    adImages.forEach(image => {
      formData.append('ads', image);
    });

    try {
      const response = await api.post('/ads', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Ad images uploaded successfully:', response.data);
      notifySuccess('Ad images uploaded successfully!');
      setShowAdUploadModal(false);
      await fetchDeckAds();
      await fetchDeckFeatures(); // Refresh features after uploading ads
    } catch (error) {
      console.error('Error uploading ad images:', error);
      notifyError('Error uploading ad images.');
    }
  };

  const handleDeleteAd = async (adId) => {
    try {
      await api.delete(`/ads/${adId}`);
      notifySuccess('Ad image deleted successfully!');
      setDeckAds(prev => prev.filter(ad => ad.ad_id !== adId));
    } catch (error) {
      console.error('Error deleting ad image:', error);
      notifyError('Error deleting ad image.');
    }
  };

  return (
    <>
      <tr>
        <td>{deckLabel}</td>
        <td>{deck.sitesRunning}</td>
        <td className="deck-actions">
          <button className="deck-btn" onClick={handleEdit}>
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button 
            className="deck-btn"
            onClick={handleView}
            title="View Deck"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            <FontAwesomeIcon icon={hovered ? faEye : faEyeSlash} />
          </button>
          {deck.deckId && (
            <button className="deck-btn" onClick={handleDelete} title="Delete Deck">
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
        </td>
        <td>
          <div className="assignment-trigger" onClick={openAssignmentModal}>
            <div className="assignment-display">
              <span className="assignment-count">{assignedStores.length}</span>
              {assignedStores.slice(0, 2).map(store => (
                <span key={store.store_id} className="preview-tag">{store.name}</span>
              ))}
              {assignedStores.length > 2 && (
                <span className="more-indicator">+{assignedStores.length - 2} more</span>
              )}
              {assignedStores.length === 0 && (
                <span className="no-assignments">No sites assigned</span>
              )}
            </div>
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </div>
        </td>
      </tr>

      {/* Deck Edit Modal */}
      {showModal && (
        <GenericModal
          size='xl'
          show={showModal}
          onClose={() => setShowModal(false)}
          header={
            <div className="custom-modal-header">
              <div className="modern-edit-icon">
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                  />
                </svg>
              </div>
              <div className="deck-modal-header-content">
                <h1 className='slide1-main-title'>Deck Editor</h1>
                <p className='slide1-subtitle' style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Customize your slide</p>
              </div>
              {isEditingDeckName ? (
                <input
                  type="text"
                  value={deckLabel}
                  onChange={handleDeckNameChange}
                  onBlur={() => setIsEditingDeckName(false)}
                  className="deck-name-input"
                  autoFocus
                />
              ) : (
               <div>
                 <div className="deck-name-display">
                  <span className="deck-name-text">{deckLabel}</span>
                  <button className='deck-name-edit-btn' onClick={() => setIsEditingDeckName(true)}><FontAwesomeIcon icon={faEdit} className="edit-icon" /></button>
                </div>
               </div>
              )}
            </div>
          }
          body={
            <>
              {/* NEW: Deck Features Panel */}
              {!viewDeck && (
                <div className="deck-features-panel">
                 
                  <p className="features-subtitle">
                    Control which features are active on this deck
                  </p>

                  <div className="features-grid">
                    {/* Complaint Slide Toggle */}
                    <div className={`feature-item ${deckFeatures.complaint_slide_enabled ? 'enabled' : ''}`}>
                      <div className="feature-header">
                        <div className="feature-icon">💬</div>
                        <div className="feature-info">
                          <div className="feature-name">Complaint Slide</div>
                          <div className="feature-description">
                            {complaintSetExists 
                              ? `${complaintOptions.length} complaint option${complaintOptions.length !== 1 ? 's' : ''} configured`
                              : 'Will create with default options'}
                          </div>
                        </div>
                      </div>
                      <label className="feature-toggle">
                        <input
                          type="checkbox"
                          checked={deckFeatures.complaint_slide_enabled}
                          onChange={(e) => handleFeatureToggle('complaint_slide_enabled', e.target.checked)}
                          className="feature-toggle-input"
                        />
                        <span className="feature-toggle-slider"></span>
                      </label>
                    </div>

                    {/* Google Review Slide Toggle */}
                    <div className={`feature-item ${deckFeatures.google_review_slide_enabled ? 'enabled' : ''}`}>
                      <div className="feature-header">
                        <div className="feature-icon">
                          <FaGoogle style={{ color: '#4285f4' }} />
                        </div>
                        <div className="feature-info">
                          <div className="feature-name">Google Review Slide</div>
                          <div className="feature-description">
                            {slides?.length >= 2 
                              ? 'Google Review slide added'
                              : 'Not added yet'}
                          </div>
                        </div>
                      </div>
                      <label className="feature-toggle">
                        <input
                          type="checkbox"
                          checked={deckFeatures.google_review_slide_enabled}
                          onChange={(e) => handleFeatureToggle('google_review_slide_enabled', e.target.checked)}
                          disabled={slides?.length < 2}
                          className="feature-toggle-input"
                        />
                        <span className="feature-toggle-slider"></span>
                      </label>
                    </div>

                    {/* Ad Images Toggle */}
                    <div className={`feature-item ${deckFeatures.ads_enabled ? 'enabled' : ''}`}>
                      <div className="feature-header">
                        <div className="feature-icon">🎨</div>
                        <div className="feature-info">
                          <div className="feature-name">Ad Images</div>
                          <div className="feature-description">
                            {deckAds?.length > 0 
                              ? `${deckAds.length} ad image${deckAds.length > 1 ? 's' : ''} uploaded`
                              : 'No ads uploaded'}
                          </div>
                        </div>
                      </div>
                      <label className="feature-toggle">
                        <input
                          type="checkbox"
                          checked={deckFeatures.ads_enabled}
                          onChange={(e) => handleFeatureToggle('ads_enabled', e.target.checked)}
                          className="feature-toggle-input"
                        />
                        <span className="feature-toggle-slider"></span>
                      </label>
                    </div>

                    {/* Complement Slide Toggle (Future Feature) */}
                    <div className="feature-item feature-item-disabled">
                      <div className="feature-header">
                        <div className="feature-icon">⭐</div>
                        <div className="feature-info">
                          <div className="feature-name">
                            Complement Slide
                            <span className="coming-soon-badge">Coming Soon</span>
                          </div>
                          <div className="feature-description">
                            Positive feedback slide (future feature)
                          </div>
                        </div>
                      </div>
                      <label className="feature-toggle">
                        <input
                          type="checkbox"
                          checked={deckFeatures.complement_slide_enabled}
                          onChange={(e) => handleFeatureToggle('complement_slide_enabled', e.target.checked)}
                          disabled={true}
                          className="feature-toggle-input"
                        />
                        <span className="feature-toggle-slider"></span>
                      </label>
                    </div>

                    {/* Polling Slide Toggle */}
                    <div className={`feature-item ${deckFeatures.polling_slide_enabled ? 'enabled' : ''}`}>
                      <div className="feature-header">
                        <div className="feature-icon">📊</div>
                        <div className="feature-info">
                          <div className="feature-name">Polling Slide</div>
                          <div className="feature-description">
                            Brand preference poll questions
                          </div>
                        </div>
                      </div>
                      <label className="feature-toggle">
                        <input
                          type="checkbox"
                          checked={deckFeatures.polling_slide_enabled}
                          onChange={(e) => handleFeatureToggle('polling_slide_enabled', e.target.checked)}
                          className="feature-toggle-input"
                        />
                        <span className="feature-toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Slide Management Section */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="select-delete-slide-container">
                  <div>
                    <div className="deck-slides-button-group">
                      <button
                        type="button"
                        className="deck-slide-select-button"
                        onClick={() => {
                          setShowAdUploadModal(true);
                          setShowPollingSlide(false);
                        }}
                      >
                        {deckAds.length > 0 ? "Update your Ad images" : "Upload your Ad images"}
                      </button>
                      {availableSlides.map((slideNum) => (
                        <button
                          key={slideNum}
                          type="button"
                          className={`deck-slide-select-button ${
                            selectedSlideNumber === slideNum ? 'selected' : ''
                          }`}
                          onClick={() => handleSlideSelection(slideNum)}
                        >
                          {slideNum === 1 && (
                            <span role="img" aria-label="smile" style={{ marginRight: '0.5em' }}>
                              😊
                            </span>
                          )}
                          {slideNum === 2 && (
                            <span role="img" aria-label="google" style={{ marginRight: '0.5em' }}>
                              <FaGoogle className="google-letter" />
                            </span>
                          )}
                          {slideNum === 1
                            ? 'Smile Score Slide'
                            : slideNum === 2
                            ? 'Google Review Slide'
                            : 'Other Slide'}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="slide-management-actions">
                    {!viewDeck && slides?.length > 1 && selectedSlideNumber !== 1 && (
                      <button
                        className="delete-slide-btn"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Are you sure you want to delete Slide ${selectedSlideNumber}? This action cannot be undone.`
                            )
                          ) {
                            deleteSlideHandler(selectedSlideNumber);
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    )}

                    {slides.length < 2 && selectedSlide?.slide_id !== 'sample' && (
                      <button
                        className={`add-google-btn ${showGoogleModal ? 'cancel-google-btn' : ''}`}
                        onClick={handleAddSlide}
                      >
                        {!showGoogleModal && <span style={{ fontSize: '18px' }}>➕</span>}
                        <FaGoogle size={20} />
                        <span>{showGoogleModal ? 'Cancel' : 'Add Google Review Slide'}</span>
                      </button>
                    )}

                    {/* Complaint Slide Button - Simplified (toggle now in features panel) */}
                    {!viewDeck && selectedSlide?.slide_id !== 'sample' && (
                      <button
                        type='button'
                        className={`deck-slide-select-button ${showComplaintModal ? 'slide-tab-active' : ''}`}
                        onClick={() => {
                          setShowComplaintModal(true);
                          setShowPollingSlide(false);
                        }}
                        title={complaintSetExists ? "Update complaint options" : "Add complaint slide to your deck"}
                      >
                        <span style={{ fontSize: '12px', marginRight: '0.5rem' }}>💬</span>
                        <span>{complaintSetExists ? 'Update Complaint Slide' : 'Add Complaint Slide'}</span>
                      </button>
                    )}

                    {/* Polling Slide Button */}
                    {!viewDeck && selectedSlide?.slide_id !== 'sample' && (
                      <button
                        type='button'
                        className={`deck-slide-select-button ${showPollingSlide ? 'slide-tab-active' : ''}`}
                        onClick={handleOpenPollingSlide}
                        title="Configure polling slide questions"
                      >
                        <span style={{ fontSize: '12px', marginRight: '0.5rem' }}>📊</span>
                        <span>Polling Slide</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {!showPollingSlide && selectedSlideNumber === 1 && !addSlide && (
                <Slide1Edit
                  selectedSlide={slides[0] || sampleSlide1}
                  deckId={deck.deckId}
                  updatedDeckName={deckLabel}
                  viewDeck={viewDeck}
                  saveEditedSlide={saveEditedSlide}
                />
              )}

              {!showPollingSlide && selectedSlideNumber === 2 && slides.length > 1 && !addSlide && (
                <GoogleReviewSlideEdit
                  isNewSlide={false}
                  selectedSlide={slides[1]}
                  totalSlides={slides.length}
                  deckId={deck.deckId}
                  updatedDeckName={deckLabel}
                  slide1={slides[0] || sampleSlide1}
                  viewDeck={viewDeck}
                  setAddSlide={setAddSlide}
                  saveEditedSlide={saveEditedSlide}
                />
              )}

              {!showPollingSlide && addSlide && slides.length < 2 && (
                <GoogleReviewSlideEdit
                  isNewSlide={true}
                  selectedSlide={getNewSlide()}
                  totalSlides={slides.length}
                  deckId={deck.deckId}
                  updatedDeckName={deckLabel}
                  slide1={slides[0] || sampleSlide1}
                  viewDeck={viewDeck}
                  setAddSlide={setAddSlide}
                  saveEditedSlide={saveEditedSlide}
                />
              )}

              {showPollingSlide && !viewDeck && (
                <PollingEdit
                  poll={pollingData}
                  deckId={deck.deckId}
                  deviceId={localStorage.getItem('deviceId')}
                  storeId={localStorage.getItem('selectedStoreId')}
                  onSaveSuccess={handlePollingSlideSuccess}
                  viewMode={false}
                />
              )}
            </>
          }
        />

      )}

      {/* Confirmation Modal for Delete Deck */}
      <Modal show={showConfirmDeleteModal} onHide={() => setShowConfirmDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deckToDelete && (
            <p>Are you sure you want to delete the deck: <strong>{deckToDelete.deckName}</strong>?</p>
          )}
          <p>This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setShowConfirmDeleteModal(false)}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={handleConfirmDelete}>
            Delete
          </button>
        </Modal.Footer>
      </Modal>

      {/* Site Assignment Modal */}
      <Modal 
        show={showAssignmentModal} 
        onHide={closeAssignmentModal} 
        centered 
        size="lg"
        className="assignment-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Assign Sites to {deckLabel}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="assignment-modal-body">
          <div className="assignment-modal-controls">
            <div className="search-box">
              <input
                type="text"
                className="search-input"
                placeholder="Search sites..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="quick-actions">
              <button className="quick-action-btn" onClick={selectAllSites}>
                Select All
              </button>
              <button className="quick-action-btn" onClick={selectNoSites}>
                Select None
              </button>
            </div>
          </div>
          
          <div className="sites-grid">
            {filteredStores.length > 0 ? (
              filteredStores.map(store => (
                <div 
                  key={store.store_id} 
                  className={`site-card ${tempSelectedStoreIds.includes(store.store_id) ? 'selected' : ''}`}
                  onClick={() => toggleTempSiteAssignment(store.store_id)}
                >
                  <div className={`site-checkbox ${tempSelectedStoreIds.includes(store.store_id) ? 'checked' : ''}`}>
                    {tempSelectedStoreIds.includes(store.store_id) && <span>✓</span>}
                  </div>
                  <div className="site-info">
                    <div className="site-name">{store.name || store.store_name} ({store.store_id})</div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ 
                gridColumn: '1 / -1', 
                padding: '20px', 
                background: '#fee2e2', 
                border: '1px solid #fecaca',
                borderRadius: '8px',
                color: '#dc2626'
              }}>
                <h4>Debug Info:</h4>
                <p>Total stores available: {stores.length}</p>
                <p>Filtered stores: {filteredStores.length}</p>
                <p>Search term: "{searchTerm}"</p>
                <div style={{ marginTop: '10px', fontSize: '12px' }}>
                  <strong>All stores:</strong>
                  <pre style={{ fontSize: '11px', background: 'white', padding: '8px', marginTop: '4px' }}>
                    {JSON.stringify(stores.map(s => ({ id: s.store_id, name: s.name || s.store_name })), null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
          
          {filteredStores.length === 0 && (
            <div className="no-sites-found">
              <p>No sites found matching "{searchTerm}"</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="assignment-modal-footer">
          <div className="selected-count-display">
            {tempSelectedStoreIds.length} sites selected
          </div>
          <div className="modal-actions">
            <button 
              className="btn btn-secondary" 
              onClick={closeAssignmentModal}
              disabled={loadingStoreDeckAssignment}
            >
              Cancel
            </button>
            <button 
              className="btn btn-primary" 
              onClick={saveAssignments}
              disabled={loadingStoreDeckAssignment}
            >
              {loadingStoreDeckAssignment ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Ad Upload Modal */}
      <AdUploadModal
        show={showAdUploadModal}
        onClose={() => setShowAdUploadModal(false)}
        deckId={deck.deckId}
        onUpload={handleAdImageUpload}
        existingAds={deckAds}
        onDeleteAd={handleDeleteAd}
      />
      {/* Complaint Modal */}
      <ComplaintModal
        show={showComplaintModal}
        onHide={() => setShowComplaintModal(false)}
        deckId={deck.deckId}
        existingComplaints={complaintOptions}
        onSave={handleComplaintSave}
      />
    </>
  );
};

export default Deck;